import { useCallback, useEffect, useState } from "react";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import LoaderBox from "@entrilia/shared/components/LoaderBox.tsx";
import { logError } from "@entrilia/shared/logging.ts";
import biApi from "../../../../../../api/biApi.ts";
import type { ReportInfo } from "../../../../../../api/types/svTypes.ts";
import { useReportsContext } from "../../contexts/reports/ReportsContext.ts";
import { GroupsContextProvider } from "../../groups/contexts/GroupsContextProvider.tsx";
import useGroups from "../../groups/hooks/useGroups.ts";
import OpenGeneratedApiReport from "./OpenGeneratedApiReport.tsx";
import SaveReportForm from "./SaveReportForm.tsx";

interface Props {
  report: ReportInfo;
  openInNewTab?: boolean;
  generateReport: (report: ReportInfo, name: string) => Promise<ReportInfo | undefined>;
  onChanged: (reportId: string) => void;
  onClose: () => void;
}

const GenerateApiReport = ({ report, openInNewTab, generateReport, onChanged, onClose }: Props) => {
  const { groups: apiReportGroups, actions: groupActions, ui: groupsUi } = useGroups({ ...biApi.apiReportGroup });

  const [generatedApiReport, setGeneratedApiReport] = useState<ReportInfo>();

  const {
    actions: { checkUniqueReportNameByUsage },
  } = useReportsContext();

  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const validateReportName = useCallback(
    async (name: string) => {
      setSaving(true);
      try {
        const isUnique = await checkUniqueReportNameByUsage(
          { reportId: undefined, name, clientCode: report.clientCode },
          "Api"
        );
        if (isUnique) {
          return true;
        } else {
          setError("A report with this name already exists");
          return false;
        }
      } catch (e) {
        logError(e, "[GenerateApiReport] checkUniqueReportNameByUsage");
        setError(" An error occurred while generating the API report.");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [checkUniqueReportNameByUsage, report.clientCode]
  );

  const onSaveClicked = useCallback(
    async (name: string, groupId: string) => {
      if (await validateReportName(name)) {
        setSaving(true);

        generateReport({ ...report, groupId }, name)
          .then((response) => {
            onChanged(response?.reportId || "");
            setGeneratedApiReport(response);
          })
          .catch(() => {
            setError(" An error occurred while generating the API report.");
          })
          .finally(() => setSaving(false));
      }
    },
    [generateReport, report, onChanged, validateReportName]
  );
  useEffect(() => {
    groupActions.load(report.clientCode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [report.clientCode, groupActions.load]);

  const isLoading = !groupsUi.loaded && groupsUi.error === undefined;

  return (
    <PromptDialog open={true}>
      <LoaderBox overlay loading={isLoading}>
        {!generatedApiReport && (
          <GroupsContextProvider groups={apiReportGroups} ui={groupsUi} actions={groupActions}>
            <SaveReportForm
              report={{ ...report, groupId: apiReportGroups[report.clientCode]?.[0]?.id }}
              error={error || (groupsUi?.error ? "Failed to load report groups" : undefined)}
              title={"Generate API Report"}
              saveBtnTitle={"Save"}
              saving={saving}
              showName
              showLevel
              onClose={onClose}
              onSave={onSaveClicked}
              groups={apiReportGroups[report.clientCode]}
              disabled={groupsUi.loaded && groupsUi.error !== undefined}
            />
          </GroupsContextProvider>
        )}

        {generatedApiReport && (
          <OpenGeneratedApiReport onClose={onClose} report={generatedApiReport} openInNewTab={openInNewTab} />
        )}
      </LoaderBox>
    </PromptDialog>
  );
};

export default GenerateApiReport;
